import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Copy } from 'lucide-react';
import { assinaturaDeTela, reportarOcorrencia } from '@/lib/ocorrencias';
import { diagnosticarChunk, isChunkError } from '@/lib/chunkRecarrega';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  registro: string | null;
}

export default class ErrorBoundary extends React.Component<Props, State> {
  /**
   * Assinatura do último erro reportado. O componentDidCatch dispara de novo a
   * cada tentativa de re-render; sem isto o mesmo erro registraria ocorrência
   * em looping.
   */
  private ultimoReportado: string | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null, registro: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? null });

    // Tela que quebra era a falha mais opaca do sistema: a pessoa via "algo deu
    // errado", recarregava, e não sobrava registro de nada. A pilha de
    // componentes do React é a parte que diz em qual tela e em qual componente
    // quebrou — e ela sobrevive melhor à minificação que a pilha de JavaScript.
    const assinatura = assinaturaDeTela(error);
    if (this.ultimoReportado === assinatura) return;
    this.ultimoReportado = assinatura;

    const detalhe: Record<string, unknown> = {
      rota: window.location.pathname,
      erro: `${error.name}: ${error.message}`.slice(0, 300),
      componentes: (errorInfo.componentStack ?? '').slice(0, 2000),
    };

    // Se a tela quebrar antes do login não há sessão e a porta responde 401: o
    // registro se perde, e isso é aceitável — o console ainda tem o erro.
    const enviar = (d: Record<string, unknown>) =>
      reportarOcorrencia('ecoweb.tela_quebrou', {
        assinatura,
        origem: `Tela ${window.location.pathname}`,
        detalhe: d,
        stack: error.stack ?? null,
      }).then((id) => {
        if (id) this.setState({ registro: id });
      });

    if (!isChunkError(error)) {
      void enviar(detalhe);
      return;
    }

    // Chunk ausente que chega até aqui já passou pelo lazyRetry sem se
    // recuperar sozinho (ver chunkRecarrega.ts). A mensagem é a mesma para
    // arquivo que o deploy levou e para arquivo que está no servidor e não
    // chegou ao navegador, então a tela pergunta de novo antes de registrar —
    // e diz se a recarga automática rodou. O navegador entra para distinguir
    // armazenamento bloqueado, que impede a recarga.
    const userAgent = navigator.userAgent;
    diagnosticarChunk(error).then(
      (diagnostico) => enviar({ ...detalhe, ...diagnostico, userAgent }),
      () => enviar({ ...detalhe, userAgent }),
    );
  }

  private copiarParaTI = async () => {
    const { error, componentStack, registro } = this.state;
    const texto = [
      'EcoWeb — tela quebrou ao carregar',
      `Rota: ${window.location.pathname}`,
      `Data/hora: ${new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}`,
      registro ? `Registro: ${registro}` : 'Registro: não foi possível gravar',
      `Erro: ${error?.name}: ${error?.message}`,
      '',
      'Pilha:',
      error?.stack || '(sem pilha)',
      '',
      'Componentes:',
      componentStack || '(sem pilha de componentes)',
    ].join('\n');

    try {
      await navigator.clipboard.writeText(texto);
    } catch {
      // Área de transferência bloqueada: o texto já está na tela, no bloco de
      // detalhes, e dá para selecionar à mão.
      console.error('[ErrorBoundary] não foi possível copiar');
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full space-y-4 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Algo deu errado</h2>
            <p className="text-sm text-muted-foreground">
              Ocorreu um erro inesperado. Tente recarregar a página.
            </p>
            {this.state.registro && (
              <p className="text-xs text-muted-foreground">
                O TI já foi avisado. Registro{' '}
                <span className="font-mono">{this.state.registro}</span>.
              </p>
            )}
            <div className="flex gap-2 justify-center flex-wrap">
              <Button onClick={() => window.location.reload()}>Recarregar Página</Button>
              <Button
                variant="outline"
                onClick={() => this.setState({
                  hasError: false, error: null, componentStack: null, registro: null,
                })}
              >
                Tentar Novamente
              </Button>
              <Button variant="outline" onClick={this.copiarParaTI}>
                <Copy className="h-4 w-4 mr-2" />
                Copiar para o TI
              </Button>
            </div>
            {this.state.error && (
              <details className="text-left mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer">Detalhes do erro</summary>
                <pre className="text-xs mt-2 p-2 bg-muted rounded overflow-auto max-h-64">
                  {this.state.error.message}
                  {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
                  {this.state.componentStack ? `\n\nComponentes:${this.state.componentStack}` : ''}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
